import type { ActionFieldDefinition } from './types';
import { z } from 'zod';

export type FieldSchema = {
  name: string;
  label: string;
  type: 'text' | 'number' | 'password' | 'select' | 'tel' | 'email' | 'date';
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: Array<{ value: string; label: string }>;
  /** For type 'number': '1' = integers only, 'any' = allow decimals */
  step?: string;
  validation?: z.ZodTypeAny;
};

export function buildFieldSchema(fieldName: string, definition: ActionFieldDefinition): FieldSchema {
  const type = toInputType(definition.type);
  const required = definition.required ?? true;
  const label = definition.legend ?? prettify(fieldName);

  const schema = z
    .string()
    .min(required ? 1 : 0, `${label} is required`)
    .refine((value) => {
      if (!definition.preg || value.length === 0) {
        return true;
      }
      const pattern = definition.preg;
      const flags = pattern.includes('\\p{') ? 'u' : '';
      const regexp = new RegExp(pattern, flags);
      return regexp.test(value);
    }, `${label} does not match format`);

  const step =
    type === 'number'
      ? definition.type === 'integer'
        ? '1'
        : 'any'
      : undefined;

  return {
    name: fieldName,
    label,
    type,
    required,
    placeholder: definition.placeholder,
    description: definition.description,
    options: definition.options,
    step,
    validation: schema
  };
}

function toInputType(domainType: ActionFieldDefinition['type']): FieldSchema['type'] {
  switch (domainType) {
    case 'password':
      return 'password';
    case 'number':
    case 'integer':
    case 'float':
      return 'number';
    case 'date':
      return 'date';
    case 'phone':
      return 'tel';
    case 'email':
      return 'email';
    case 'select':
      return 'select';
    case 'string':
    default:
      return 'text';
  }
}

function prettify(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

